import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { formatDateForApi } from '@/shared/lib/utils';
import {
  excludeFieldsUsedByOtherRoutes,
  getValueRouteFields,
  isFieldUsedByOtherRoute,
} from '../lib/archiving-fields';
import { getDateFields } from '../lib/matrix-fields';
import {
  MAX_ARCHIVING_ROUTES,
  buildArchivingRoutesPayload,
  buildArchivingSegments,
  buildExamplePath,
  buildTokenPath,
  routesFromPayload,
} from '../lib/archiving-path';
import type {
  ArchivingField,
  ArchivingModel,
  ArchivingRoute,
  ArchivingRouteDto,
} from './archiving.types';

export interface ArchivingRoutesSeed {
  model: ArchivingModel | null;
  routes: ArchivingRouteDto[];
}

const HELPER_TEXT: Record<ArchivingModel, string> = {
  ARCHIVE_YEAR:
    'Escolha o campo de data. Para adicionar outras rotas, use o modelo com opções genéricas.',
  ARCHIVE_MONTH:
    'Escolha o campo de data. Para adicionar outras rotas, use o modelo com opções genéricas.',
  ARCHIVE_GENERIC: 'Até 4 rotas. Um campo usado em uma rota não aparece nas outras.',
};

export function useArchivingRoutes(
  fields: Ref<ArchivingField[]> | ComputedRef<ArchivingField[]>,
  initial: ArchivingRoutesSeed
) {
  let nextKey = 1;
  const model = ref<ArchivingModel | null>(null);
  // Holds the generic routes even while another model hides them, so switching back restores them.
  const routes = ref<ArchivingRoute[]>([]);

  function newRoute(fieldName = ''): ArchivingRoute {
    return { key: nextKey++, fieldName };
  }

  function reset(seed: ArchivingRoutesSeed) {
    model.value = seed.model;
    const seeded = routesFromPayload(seed.routes).map(newRoute);
    if (seed.model === 'ARCHIVE_GENERIC' && seeded.length < 2) seeded.push(newRoute());
    routes.value = seeded;
  }

  reset(initial);

  const dateFields = computed(() => getDateFields(fields.value));
  const valueFields = computed(() => getValueRouteFields(fields.value));

  const isGeneric = computed(() => model.value === 'ARCHIVE_GENERIC');

  const visibleRoutes = computed(() => (isGeneric.value ? routes.value : routes.value.slice(0, 1)));

  const firstRoute = computed(() => routes.value[0]);

  const genericRoutes = computed({
    get: () => routes.value.slice(1),
    set: (value: ArchivingRoute[]) => {
      routes.value = [routes.value[0], ...value];
    },
  });

  const canAddRoute = computed(() => isGeneric.value && routes.value.length < MAX_ARCHIVING_ROUTES);

  const segments = computed(() => buildArchivingSegments(model.value, routes.value));
  const tokenPath = computed(() => buildTokenPath(segments.value));

  const exampleValues = computed<Record<string, string>>(() => {
    const dateFieldName = firstRoute.value.fieldName;
    const today = formatDateForApi(new Date());
    return dateFieldName !== '' && today !== null ? { [dateFieldName]: today } : {};
  });
  const examplePath = computed(() => buildExamplePath(segments.value, exampleValues.value));

  const helperText = computed(() => (model.value === null ? '' : HELPER_TEXT[model.value]));

  const unknownFieldNames = computed(() => {
    const known = new Set(fields.value.map((field) => field.name));
    return visibleRoutes.value
      .map((route) => route.fieldName)
      .filter((fieldName) => fieldName !== '' && !known.has(fieldName));
  });

  const payload = computed(() => ({
    model: model.value,
    routes: buildArchivingRoutesPayload(model.value, routes.value),
  }));

  function setModel(next: ArchivingModel | null) {
    model.value = next;
    if (next === 'ARCHIVE_GENERIC' && routes.value.length < 2) {
      routes.value = [...routes.value, newRoute()];
    }
  }

  function availableFields(index: number): ArchivingField[] {
    const pool = index === 0 ? dateFields.value : valueFields.value;
    return excludeFieldsUsedByOtherRoutes(pool, routes.value, index);
  }

  function isGenericRoute(index: number): boolean {
    return isGeneric.value && index >= 1 && index < routes.value.length;
  }

  function canRemoveRoute(index: number): boolean {
    return isGenericRoute(index);
  }

  function addRoute() {
    if (!canAddRoute.value) return;
    routes.value = [...routes.value, newRoute()];
  }

  function removeRoute(index: number) {
    if (!canRemoveRoute(index)) return;
    const remaining = routes.value.filter((_, routeIndex) => routeIndex !== index);
    routes.value = remaining.length < 2 ? [...remaining, newRoute()] : remaining;
  }

  function moveRoute(from: number, to: number) {
    if (!isGenericRoute(from) || !isGenericRoute(to) || from === to) return;

    const next = [...routes.value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    routes.value = next;
  }

  function setRouteField(index: number, fieldName: string) {
    const route = routes.value[index];
    if (!route) return;
    if (fieldName !== '' && isFieldUsedByOtherRoute(routes.value, index, fieldName)) return;

    routes.value = routes.value.map((item, routeIndex) =>
      routeIndex === index ? { ...item, fieldName } : item
    );
  }

  return {
    model,
    setModel,
    routes,
    visibleRoutes,
    isGeneric,
    firstRoute,
    genericRoutes,
    canAddRoute,
    canRemoveRoute,
    availableFields,
    addRoute,
    removeRoute,
    moveRoute,
    setRouteField,
    tokenPath,
    examplePath,
    helperText,
    unknownFieldNames,
    payload,
    reset,
  };
}
